import { IGoal, IWorkflowActions, IWorkflowUi } from "./models";

// exported so function can be tested
export function checkIfGoalIsApprovedByCurrentUser(goal: IGoal, currentUserId: string): boolean {
  return !!goal.workflow.reviewers.find((reviewer) => reviewer.id === currentUserId && reviewer.review === "approves");
}

// exported so function can be tested
export function checkIfGoalIsDeclined(goal: IGoal): boolean {
  return !!goal.workflow.reviewers.find((reviewer) => reviewer.review === "declines");
}

export function generateWorkflowUi(actions: IWorkflowActions, goal: IGoal, currentUserId: string): IWorkflowUi {
  // if workflow is not set -> don't show workflow related stuff
  if (!actions || actions.none === true) {
    return null;
  }

  const workflowUi: IWorkflowUi = {
    actions: null,
    // owner is needed to reduce conditions count for tooltip text
    owner: actions.owner,
    view: {
      buttons: {
        approve: {
          show: false,
          hide: false,
        },
        decline: {
          show: false,
          hide: false,
        },
        withdraw: {
          show: false,
          hide: false,
        },
      },
    },
    limitReviewers: 5,
    defaultLimitReviewers: 5,
    showAllReviewers: false,
  };

  // if in approved state -> show withdraw or decline button based on ownership
  // if owner - withdraw button, if reviewer - decline button
  if (actions.approved) {
    workflowUi.actions = {
      approved: {
        owner: actions.owner,
        reviewer: actions.reviewer,
      },
    };

    return workflowUi;
  }

  // if in draft state
  if (actions.draft) {
    // and the current user is random(not the goal owner) -> show workflow-bar but with no actions
    workflowUi.actions = {
      draft: {},
    };

    // and the current user is the goal owner -> show actions
    // setting declined because of the different status text
    if (actions.owner) {
      workflowUi.actions = {
        draft: {
          owner: true,
          declined: checkIfGoalIsDeclined(goal),
        },
      };
    }

    return workflowUi;
  }

  // if in inReview state
  if (actions.review) {
    // and current user is random(not the goal owner and not a reviewer) -> show workflow-bar but with no actions
    workflowUi.actions = {
      inReview: {},
    };

    // and the current user is reviewer -> show actions
    if (actions.reviewer) {
      workflowUi.actions.inReview = {
        reviewer: true,
        approves: checkIfGoalIsApprovedByCurrentUser(goal, currentUserId),
      };
    }

    // and the current user is owner -> show actions
    if (actions.owner) {
      workflowUi.actions.inReview.owner = true;
    }

    return workflowUi;
  }

  // if in notEnoughReviewers state
  if (actions.needsReviewers) {
    // and current user is random -> show workflow-bar but with no actions
    workflowUi.actions = {
      notEnoughReviewers: {},
    };

    // and the current user is reviewer -> show actions
    if (actions.reviewer) {
      workflowUi.actions = {
        notEnoughReviewers: {
          reviewer: true,
          approves: checkIfGoalIsApprovedByCurrentUser(goal, currentUserId),
        },
      };
    }

    // and the current user is the goal owner -> show actions
    if (actions.owner) {
      workflowUi.actions.notEnoughReviewers.owner = true;
    }

    return workflowUi;
  }
}

export function applyWorkflowConditions(workflowUi: IWorkflowUi): IWorkflowUi {
  if (workflowUi.actions) {
    // show approve button
    if (
      (workflowUi.actions.inReview && workflowUi.actions.inReview.reviewer && !workflowUi.actions.inReview.approves) ||
      (workflowUi.actions.notEnoughReviewers && workflowUi.actions.notEnoughReviewers.reviewer && !workflowUi.actions.notEnoughReviewers.approves)
    ) {
      workflowUi.view.buttons.approve.show = true;
    }

    // show decline button
    if (
      (workflowUi.actions.inReview && workflowUi.actions.inReview.reviewer) ||
      (workflowUi.actions.notEnoughReviewers && workflowUi.actions.notEnoughReviewers.reviewer) ||
      (workflowUi.actions.approved && workflowUi.actions.approved.reviewer)
    ) {
      workflowUi.view.buttons.decline.show = true;
    }

    // show withdraw button
    if (
      (workflowUi.actions.inReview && workflowUi.actions.inReview.owner) ||
      (workflowUi.actions.notEnoughReviewers && workflowUi.actions.notEnoughReviewers.owner) ||
      (workflowUi.actions.approved && workflowUi.actions.approved.owner)
    ) {
      workflowUi.view.buttons.withdraw.show = true;
    }
  }

  return workflowUi;
}
